import { Suspense, useMemo, useState } from "react"
import { Outlet } from "react-router-dom"
import Navbar from "../navbar"
import Header from "../header"
import { useAppSelector } from "../../hooks"
import {
  useCanUseAssistant,
  useIsBankAdmin,
  useIsSuperAdmin
} from "../../hooks/use-user-roles"
import { selectUser } from "../../../redux/slices/auth"
import { ALLOWED_ROLES_BY_ROUTE, NavbarItems } from "../../constants"
import { getUserRole } from "../../utils"
import ErrorBoundary from "../../utils/error-boundary"
import { MobileAppPopup } from "../../ui"
import PageLoader from "../../ui/page-loader"
import StaffAiDrawer from "../../ui/staff-ai-chat/drawer"
import { StaffChatSessionProvider } from "../../ui/staff-ai-chat/session-context"
import { useFindChannelsQuery } from "../../../redux/queries"

const FullLayout = () => {
  const user = useAppSelector(selectUser)
  const isSuperAdmin = useIsSuperAdmin()
  const isBankAdmin = useIsBankAdmin()
  const canUseAssistant = useCanUseAssistant()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  const toggleSidebarCollapsed = () => setSidebarCollapsed(!sidebarCollapsed)

  const orgId = user?.user?.org?.id ?? ""
  const { data: channelsForNav = [] } = useFindChannelsQuery(
    { org_id: orgId },
    { skip: !isBankAdmin || !orgId }
  )
  const hasActiveChannel = channelsForNav.some(c => c.status === "active")

  const userRole = getUserRole(user?.user.roles ?? [])
  const navItems = useMemo(() => {
    const items = userRole
      ? NavbarItems[userRole].filter(({ key, hidden }) => {
          if (
            !userRole ||
            hidden ||
            !ALLOWED_ROLES_BY_ROUTE[key].includes(userRole)
          ) {
            return false
          }
          if (key === "channel-leads" && isBankAdmin && !isSuperAdmin) {
            return hasActiveChannel
          }
          return true
        })
      : []

    return items.map(item =>
      item.key === "dashboard" && !isSuperAdmin
        ? { ...item, name: "Dashboard" }
        : item
    )
  }, [userRole, isSuperAdmin, isBankAdmin, hasActiveChannel])

  const content = (
    <div className="h-dvh">
      <Navbar
        items={navItems}
        sidebarOpen={sidebarOpen}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarOpen={setSidebarOpen}
        toggleSidebarCollapsed={toggleSidebarCollapsed}
      />
      <div
        className={`${
          sidebarCollapsed ? "lg:pl-24" : "lg:pl-64"
        } h-full min-h-0 flex flex-1 flex-col overflow-hidden bg-primary-100 transition-all`}
      >
        <Header setSidebarOpen={setSidebarOpen} />
        <main className="min-h-0 flex flex-1 flex-col overflow-auto bg-white">
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
      <MobileAppPopup />
      {canUseAssistant && <StaffAiDrawer />}
    </div>
  )

  return canUseAssistant ? (
    <StaffChatSessionProvider>{content}</StaffChatSessionProvider>
  ) : (
    content
  )
}

export default FullLayout
